import { useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, type FormEvent, useMemo, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  PageHeader,
  StatusBadge,
} from "@/components/ui";
import {
  type Document,
  errorMessage,
  useApi,
  useDocuments,
  useInvalidating,
  useWebsitePages,
  useWebsites,
  type Website,
} from "@/lib/api";

export function KnowledgeScreen() {
  const websites = useWebsites();
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useInvalidating(
    (api, body: { url: string }) => api.POST("/websites", { body }),
    [["websites"], ["knowledge"]],
  );
  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ url });
      setUrl("");
    } catch (err) {
      setError(errorMessage(err, "Could not add that website."));
    }
  };
  return (
    <>
      <PageHeader
        title="Knowledge"
        subtitle="What the assistant has read: your websites, page by page, and your documents."
      />
      <div className="flex flex-col gap-6">
        <form onSubmit={add} className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Add a website: acme.com"
            required
            className="sm:max-w-[360px]"
          />
          <Button type="submit" variant="primary" disabled={create.isPending}>
            Add website
          </Button>
        </form>
        <ErrorNote message={error} />
        {(websites.data ?? []).map((w) => (
          <PagesPanel key={w.id} website={w} />
        ))}
        <DocumentsPanel />
      </div>
    </>
  );
}

/** One website: its pages with checkboxes. Shared with onboarding. */
export function PagesPanel({ website }: { website: Website }) {
  const pages = useWebsitePages(website.id);
  const [filter, setFilter] = useState("");
  const select = useInvalidating(
    (api, body: { pageIds: string[]; selected: boolean }) =>
      api.PUT("/websites/{websiteId}/pages/selection", {
        params: { path: { websiteId: website.id } },
        body,
      }),
    [["websites"], ["knowledge"]],
  );
  const rediscover = useInvalidating(
    (api, websiteId: string) =>
      api.POST("/websites/{websiteId}/rediscover", {
        params: { path: { websiteId } },
      }),
    [["websites"]],
  );
  const remove = useInvalidating(
    (api, websiteId: string) =>
      api.DELETE("/websites/{websiteId}", { params: { path: { websiteId } } }),
    [["websites"], ["knowledge"]],
  );
  const items = pages.data?.items ?? [];
  const shown = useMemo(
    () =>
      filter
        ? items.filter((p) =>
            p.url.toLowerCase().includes(filter.toLowerCase()),
          )
        : items,
    [items, filter],
  );
  const toggleAll = (selected: boolean) => {
    const ids = shown.filter((p) => p.selected !== selected).map((p) => p.id);
    if (ids.length > 0) select.mutate({ pageIds: ids, selected });
  };

  return (
    <Card className="p-0">
      <header className="flex flex-col gap-2 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="truncate font-semibold">{website.origin}</p>
          <p className="text-[12px] text-muted">
            {website.sitemapStatus === "ready"
              ? `${website.selectedCount} of ${website.pageCount} pages selected · ${website.readyCount} read`
              : website.sitemapStatus === "failed"
                ? (website.sitemapError ?? "Could not read the site")
                : "Looking for pages…"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={website.sitemapStatus} />
          <Button
            variant="ghost"
            className="h-8 px-2 text-[12px]"
            onClick={() => rediscover.mutate(website.id)}
            disabled={website.sitemapStatus === "discovering"}
          >
            Re-scan
          </Button>
          <Button
            variant="danger"
            className="h-8 px-2 text-[12px]"
            onClick={() => remove.mutate(website.id)}
          >
            Remove
          </Button>
        </div>
      </header>
      {items.length === 0 ? (
        <EmptyState
          title={
            website.sitemapStatus === "ready"
              ? "No pages found."
              : "Pages will appear here."
          }
        />
      ) : (
        <>
          <div className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter pages"
              className="h-8 sm:max-w-[260px]"
            />
            <div className="flex gap-2 text-[12px]">
              <button
                type="button"
                className="text-accent"
                onClick={() => toggleAll(true)}
              >
                Select all
              </button>
              <span className="text-muted">·</span>
              <button
                type="button"
                className="text-accent"
                onClick={() => toggleAll(false)}
              >
                Select none
              </button>
            </div>
          </div>
          <ul className="max-h-[420px] divide-y divide-border overflow-y-auto border-t border-border">
            {shown.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-5 py-2">
                <input
                  type="checkbox"
                  checked={p.selected}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    select.mutate({
                      pageIds: [p.id],
                      selected: e.target.checked,
                    })
                  }
                  className="h-4 w-4 accent-[rgb(var(--color-accent))]"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px]">
                    {p.title ?? (p.url.replace(website.origin, "") || "/")}
                  </p>
                  <p className="truncate text-[11px] text-muted">
                    {p.url}
                    {p.error ? ` — ${p.error}` : ""}
                  </p>
                </div>
                {p.selected ? <StatusBadge status={p.status} /> : null}
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

const ACCEPT = ".pdf,.docx,.txt,.md,.csv";
const TYPES: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
};

/** Uploads: reserve → presigned POST straight to S3 → complete. */
export function DocumentsPanel() {
  const api = useApi();
  const qc = useQueryClient();
  const documents = useDocuments();
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string[]>([]);
  const remove = useInvalidating(
    (a, documentId: string) =>
      a.DELETE("/documents/{documentId}", { params: { path: { documentId } } }),
    [["documents"], ["knowledge"]],
  );

  const upload = async (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const contentType = TYPES[ext];
    if (!contentType)
      throw new Error(`"${file.name}" is not a supported type.`);
    const reserved = await api.POST("/documents", {
      body: {
        fileName: file.name,
        contentType: contentType as never,
        byteSize: file.size,
      },
    });
    if (reserved.error || !reserved.data)
      throw new Error(
        errorMessage(reserved.error, "Could not start the upload."),
      );
    const form = new FormData();
    for (const [k, v] of Object.entries(reserved.data.fields))
      form.append(k, v);
    form.append("file", file);
    const put = await fetch(reserved.data.uploadUrl, {
      method: "POST",
      body: form,
    });
    if (!put.ok) throw new Error(`Upload failed (${put.status}).`);
    const done = await api.POST("/documents/{documentId}/complete", {
      params: { path: { documentId: reserved.data.document.id } },
    });
    if (done.error)
      throw new Error(errorMessage(done.error, "Could not finish the upload."));
  };

  const onFiles = async (files: FileList | null) => {
    if (!files) return;
    setError(null);
    for (const file of Array.from(files)) {
      setUploading((u) => [...u, file.name]);
      try {
        await upload(file);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed.");
      } finally {
        setUploading((u) => u.filter((n) => n !== file.name));
        void qc.invalidateQueries({ queryKey: ["documents"] });
        void qc.invalidateQueries({ queryKey: ["knowledge"] });
      }
    }
  };

  const docs: Document[] = documents.data ?? [];
  return (
    <Card className="p-0">
      <header className="border-b border-border px-5 py-4">
        <p className="font-semibold">Documents</p>
        <p className="text-[12px] text-muted">
          {docs.length === 0 ? "None yet." : `${docs.length} uploaded`}
        </p>
      </header>
      <label
        className="m-5 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-panel border border-dashed border-border bg-bg px-6 py-8 text-center hover:border-accent"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          void onFiles(e.dataTransfer.files);
        }}
      >
        <span className="text-[14px] font-medium">
          Drop files here, or click to choose
        </span>
        <span className="text-[12px] text-muted">
          PDF, Word, text, Markdown, CSV · up to 20 MB each
        </span>
        <input
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => void onFiles(e.target.files)}
        />
      </label>
      <div className="px-5">
        <ErrorNote message={error} />
      </div>
      {uploading.length > 0 || docs.length > 0 ? (
        <ul className="divide-y divide-border border-t border-border">
          {uploading.map((name) => (
            <li
              key={`up-${name}`}
              className="flex items-center justify-between px-5 py-2 text-[13px]"
            >
              <span className="truncate">{name}</span>
              <span className="text-[12px] text-muted">uploading…</span>
            </li>
          ))}
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-5 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px]">{d.fileName}</p>
                <p className="text-[11px] text-muted">
                  {(d.byteSize / 1024).toFixed(0)} KB
                  {d.error ? ` — ${d.error}` : ""}
                </p>
              </div>
              <StatusBadge status={d.status} />
              <button
                type="button"
                className="text-[12px] text-muted hover:text-danger"
                onClick={() => remove.mutate(d.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
